// src/pages/public/ProfilePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowRight, Target, Eye, Award, Heart, Zap,
  ChevronLeft, ChevronRight, Star, Quote,
  MapPin, Users, TrendingUp, Shield, ChevronDown,
  Download, Send, MessageCircle,
} from 'lucide-react';

import MainLayout from '../../components/layout/MainLayout';
/* ── Color tokens (sama dengan HomePage) ── */
const C = {
  amber:      '#d97706',
  amberLight: '#fef3c7',
  amberDark:  '#92400e',
  amberHover: '#b45309',
  white:      '#ffffff',
  gray50:     '#f9fafb',
  gray100:    '#f3f4f6',
  gray200:    '#e5e7eb',
  gray400:    '#9ca3af',
  gray500:    '#6b7280',
  gray600:    '#4b5563',
  gray700:    '#374151',
  gray900:    '#111827',
  stone50:    '#fafaf9',
};

/* ── useInView — trigger sekali, tidak reset ── */
const useInView = (threshold = 0.12) => {
  const ref       = useRef(null);
  const [inView, setInView] = useState(false);
  const triggered = useRef(false);
  useEffect(() => {
    if (triggered.current) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !triggered.current) {
        triggered.current = true;
        setInView(true);
        obs.disconnect();
      }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

/* ── Animated wrapper ── */
const Anim = ({ children, delay = 0, dir = 'up', style = {} }) => {
  const [ref, inView] = useInView();
  const from = {
    up:    'translateY(50px)',
    left:  'translateX(-50px)',
    right: 'translateX(50px)',
    scale: 'scale(0.88)',
  };
  return (
    <div ref={ref} style={{
      opacity:    inView ? 1 : 0,
      transform:  inView ? 'none' : from[dir],
      transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms,
                   transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
};

/* ── CountUp ── */
const CountUp = ({ end, suffix = '' }) => {
  const [n, setN]     = useState(0);
  const [ref, inView] = useInView();
  const done          = useRef(false);
  useEffect(() => {
    if (!inView || done.current) return;
    done.current = true;
    const t0 = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - t0) / 2000, 1);
      setN(Math.floor((1 - Math.pow(1 - p, 3)) * end));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView]);
  return <span ref={ref}>{n}{suffix}</span>;
};

/* ── Floating Orb ── */
const Orb = ({ size, color, top, left, delay = 0 }) => (
  <div style={{
    position: 'absolute', width: size, height: size, borderRadius: '50%',
    background: color, top, left, filter: 'blur(70px)', opacity: 0.28,
    animation: `floatOrb ${7 + delay}s ease-in-out infinite alternate`,
    animationDelay: `${delay}s`, pointerEvents: 'none', zIndex: 0,
  }} />
);

/* ── SectionLabel ── */
const SectionLabel = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', marginBottom: 18 }}>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, ${C.amber}, #f59e0b)`, borderRadius: 2 }} />
    <span style={{ fontSize: 12, color: C.amber, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{text}</span>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, #f59e0b, ${C.amber})`, borderRadius: 2 }} />
  </div>
);

/* ── BtnPrimary ── */
const BtnPrimary = ({ onClick, href, children, style = {} }) => {
  const [hov, setHov] = useState(false);
  const s = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '14px 30px', borderRadius: 50, border: 'none', cursor: 'pointer',
    backgroundColor: hov ? C.amberHover : C.amber,
    color: C.white, fontWeight: 700, fontSize: 15,
    letterSpacing: '0.02em', fontFamily: 'inherit', textDecoration: 'none',
    transform: hov ? 'translateY(-2px)' : 'translateY(0)',
    boxShadow: hov ? '0 14px 32px rgba(217,119,6,0.48)' : '0 4px 16px rgba(217,119,6,0.32)',
    transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)', ...style,
  };
  const ev = { onMouseEnter: () => setHov(true), onMouseLeave: () => setHov(false) };
  if (href) return <a href={href} style={s} {...ev}>{children}</a>;
  return <button onClick={onClick} style={s} {...ev}>{children}</button>;
};

/* ── BtnOutline ── */
const BtnOutline = ({ onClick, href, children, dark = false, style = {} }) => {
  const [hov, setHov] = useState(false);
  const s = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '13px 30px', borderRadius: 50, cursor: 'pointer',
    backgroundColor: hov ? (dark ? 'rgba(255,255,255,0.1)' : 'rgba(217,119,6,0.07)') : 'transparent',
    color: dark ? C.white : C.amber,
    border: `2px solid ${dark ? 'rgba(255,255,255,0.5)' : C.amber}`,
    fontWeight: 700, fontSize: 15, letterSpacing: '0.02em', fontFamily: 'inherit', textDecoration: 'none',
    transform: hov ? 'translateY(-2px)' : 'translateY(0)',
    transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)', ...style,
  };
  const ev = { onMouseEnter: () => setHov(true), onMouseLeave: () => setHov(false) };
  if (href) return <a href={href} style={s} {...ev}>{children}</a>;
  return <button onClick={onClick} style={s} {...ev}>{children}</button>;
};

/* ── NavBtn slider ── */
const NavBtn = ({ onClick, disabled, children }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick} disabled={disabled}
      onMouseEnter={() => !disabled && setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 48, height: 48, borderRadius: '50%', border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: hov ? '0 12px 28px rgba(0,0,0,0.14)' : '0 4px 16px rgba(0,0,0,0.1)',
        transform: hov ? 'scale(1.12)' : 'scale(1)',
        opacity: disabled ? 0.4 : 1,
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      {children}
    </button>
  );
};

/* ── WhyCard ── */
const WhyCard = ({ iconBg, iconColor, Icon, emoji, title, desc, delay }) => {
  const [hov, setHov] = useState(false);
  return (
    <Anim delay={delay}>
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          background: hov ? 'rgba(217,119,6,0.04)' : C.white,
          borderRadius: 22, padding: '36px 28px', textAlign: 'center', cursor: 'default',
          boxShadow: hov ? '0 24px 60px rgba(0,0,0,0.12)' : '0 4px 24px rgba(0,0,0,0.07)',
          border: `1.5px solid ${hov ? 'rgba(217,119,6,0.22)' : C.gray100}`,
          transform: hov ? 'translateY(-10px)' : 'translateY(0)',
          transition: 'all 0.45s cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        <div style={{
          width: 72, height: 72, borderRadius: 20, backgroundColor: iconBg || C.amberLight,
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
          transform: hov ? 'scale(1.22) rotate(-8deg)' : 'scale(1)',
          transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          {emoji ? <span style={{ fontSize: 32 }}>{emoji}</span> : <Icon size={32} color={iconColor || C.amber} />}
        </div>
        <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: C.gray900, marginBottom: 10 }}>{title}</h3>
        <p style={{ fontSize: 13.5, color: C.gray500, lineHeight: 1.78 }}>{desc}</p>
      </div>
    </Anim>
  );
};

/* ── OfferCard ── */
const OfferCard = ({ Icon, iconBg, bgFrom, bgTo, borderColor, title, desc, delay }) => {
  const [hov, setHov] = useState(false);
  return (
    <Anim delay={delay}>
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          background: `linear-gradient(135deg, ${bgFrom}, ${bgTo})`,
          borderRadius: 20, padding: '32px',
          border: `2px solid ${hov ? C.amber : borderColor}`,
          transform: hov ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
          boxShadow: hov ? '0 24px 56px rgba(0,0,0,0.12)' : '0 4px 16px rgba(0,0,0,0.06)',
          transition: 'all 0.45s cubic-bezier(0.34,1.56,0.64,1)', cursor: 'default',
        }}
      >
        <div style={{
          width: 56, height: 56, borderRadius: 16, backgroundColor: iconBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18,
          transform: hov ? 'scale(1.16) rotate(-6deg)' : 'scale(1)',
          transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          <Icon size={26} color="#fff" />
        </div>
        <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 21, fontWeight: 700, color: C.gray900, marginBottom: 10 }}>{title}</h3>
        <p style={{ fontSize: 13.5, color: C.gray600, lineHeight: 1.78 }}>{desc}</p>
      </div>
    </Anim>
  );
};

/* ── PlatformBadge ── */
const PlatformBadge = ({ name, delay }) => {
  const [hov, setHov] = useState(false);
  return (
    <Anim delay={delay}>
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          background: C.white, borderRadius: 16,
          boxShadow: hov ? '0 16px 40px rgba(0,0,0,0.12)' : '0 4px 16px rgba(0,0,0,0.07)',
          border: `1.5px solid ${hov ? C.amber : C.gray100}`,
          height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
          transform: hov ? 'translateY(-6px) scale(1.05)' : 'translateY(0) scale(1)',
          transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)', cursor: 'default',
        }}
      >
        <span style={{ fontWeight: 700, color: hov ? C.amber : C.gray700, fontSize: 13.5, textAlign: 'center', padding: '0 14px', transition: 'color 0.3s' }}>{name}</span>
      </div>
    </Anim>
  );
};

/* ── PackageCard ── */
const PackageCard = ({ popular, title, subtitle, price, priceNote, items, accentColor, accentHover, delay }) => {
  const [hov, setHov]       = useState(false);
  const [btnHov, setBtnHov] = useState(false);
  return (
    <Anim delay={delay}>
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          background:   popular ? 'linear-gradient(135deg, #fffbeb, #fff7ed)' : C.white,
          borderRadius: 24, padding: '40px 36px',
          border:       popular ? `3px solid ${C.amber}` : `2px solid ${C.gray200}`,
          boxShadow:    hov ? '0 32px 70px rgba(0,0,0,0.14)' : '0 6px 28px rgba(0,0,0,0.08)',
          transform:    hov ? 'translateY(-10px)' : 'translateY(0)',
          transition:   'all 0.45s cubic-bezier(0.34,1.56,0.64,1)',
          position:     'relative',
        }}
      >
        {popular && (
          <div style={{
            position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)',
            background: C.amber, color: '#fff', padding: '6px 22px', borderRadius: 50,
            fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            boxShadow: '0 4px 14px rgba(217,119,6,0.4)',
          }}>Most Popular</div>
        )}
        <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 700, color: C.gray900, marginBottom: 6, marginTop: popular ? 14 : 0 }}>{title}</h3>
        <p style={{ fontSize: 14, color: C.gray500, marginBottom: 24 }}>{subtitle}</p>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, fontWeight: 700, color: popular ? C.amber : C.gray900, marginBottom: 4 }}>{price}</div>
        <p style={{ fontSize: 12.5, color: C.gray400, marginBottom: 28 }}>{priceNote}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 30 }}>
          {items.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>✓</span>
              </div>
              <span style={{ fontSize: 13.5, color: C.gray600, lineHeight: 1.6 }}>{item}</span>
            </div>
          ))}
        </div>
        <button
          onMouseEnter={() => setBtnHov(true)}
          onMouseLeave={() => setBtnHov(false)}
          onClick={() => window.location.href = '/contact'}
          style={{
            width: '100%', padding: '15px', borderRadius: 50, border: 'none', cursor: 'pointer',
            backgroundColor: btnHov ? accentHover : accentColor,
            color: '#fff', fontWeight: 700, fontSize: 15, fontFamily: 'inherit',
            transform: btnHov ? 'translateY(-2px)' : 'translateY(0)',
            boxShadow: btnHov ? `0 12px 28px ${accentColor}55` : `0 4px 14px ${accentColor}33`,
            transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          Choose This Package
        </button>
      </div>
    </Anim>
  );
};

/* ── ValueRow ── */
const ValueRow = ({ Icon, iconColor, iconBg, label, desc }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 16, cursor: 'default',
        padding: '10px 14px', margin: '0 -14px 4px', borderRadius: 14,
        background: hov ? 'rgba(217,119,6,0.06)' : 'transparent',
        transform: hov ? 'translateX(8px)' : 'translateX(0)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 12, backgroundColor: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        transform: hov ? 'scale(1.18) rotate(-8deg)' : 'scale(1)',
        transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)',
      }}>
        <Icon size={20} color={iconColor} />
      </div>
      <div>
        <div style={{ fontWeight: 700, color: C.gray900, fontSize: 14.5 }}>{label}</div>
        <div style={{ fontSize: 13, color: C.gray500 }}>{desc}</div>
      </div>
    </div>
  );
};

/* ── TeamCard ── */
const TeamCard = ({ member }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ position: 'relative', borderRadius: 20, overflow: 'hidden', height: 390, cursor: 'default', flexShrink: 0 }}
    >
      <img
        src={member.image} alt={member.name}
        style={{ width: '100%', height: '100%', objectFit: 'cover', transform: hov ? 'scale(1.1)' : 'scale(1)', transition: 'transform 0.8s cubic-bezier(0.16,1,0.3,1)' }}
      />
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.35) 55%, transparent)',
        opacity: hov ? 1 : 0, transition: 'opacity 0.4s ease',
      }} />
      {/* Default label */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px 22px',
        background: 'linear-gradient(to top, rgba(0,0,0,0.62), transparent)',
        opacity: hov ? 0 : 1, transition: 'opacity 0.35s ease',
      }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 21, fontWeight: 700, color: '#fff' }}>{member.name}</div>
        <div style={{ fontSize: 13, color: C.amber, fontWeight: 600 }}>{member.position}</div>
      </div>
      {/* Hover detail */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, padding: '26px 24px',
        opacity: hov ? 1 : 0,
        transform: hov ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.4s ease, transform 0.4s cubic-bezier(0.34,1.56,0.64,1)',
      }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{member.name}</div>
        <div style={{ fontSize: 13, color: C.amber, fontWeight: 700, marginBottom: 10 }}>{member.position}</div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', lineHeight: 1.65 }}>{member.bio}</div>
      </div>
    </div>
  );
};

/* ── OpBtn ── */
const OpBtn = () => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={() => window.location.href = '/contact'}
      style={{
        padding: '14px 34px', borderRadius: 50, border: 'none', cursor: 'pointer',
        backgroundColor: hov ? '#15803d' : '#16a34a',
        color: '#fff', fontWeight: 700, fontSize: 15, fontFamily: 'inherit', whiteSpace: 'nowrap',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hov ? '0 12px 28px rgba(22,163,74,0.42)' : '0 4px 16px rgba(22,163,74,0.28)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      Choose This Package
    </button>
  );
};

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
const ProfilePage = () => {
  const [currentTeamSlide, setCurrentTeamSlide] = useState(0);
  const [currentReview, setCurrentReview]       = useState(0);
  const [scrollY, setScrollY]                   = useState(0);
  const [mousePos, setMousePos]                 = useState({ x: 0, y: 0 });
  const videoRef = useRef(null);

  const teamMembers = [
    { id: 1, name: 'Sarah Johnson',    position: 'General Manager',         image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80',  bio: 'Leading our operations with 15+ years of hospitality experience' },
    { id: 2, name: 'Michael Chen',     position: 'Operations Director',     image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80',  bio: 'Ensuring seamless villa operations and guest satisfaction' },
    { id: 3, name: 'Amanda Rodriguez', position: 'Marketing Manager',       image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80', bio: 'Creating compelling campaigns that drive bookings' },
    { id: 4, name: 'David Kumar',      position: 'Property Manager',        image: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?w=400&q=80',  bio: 'Maintaining excellence in property standards' },
    { id: 5, name: 'Lisa Anderson',    position: 'Guest Relations Manager', image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&q=80', bio: 'Dedicated to creating unforgettable guest experiences' },
    { id: 6, name: 'Robert Williams',  position: 'Finance Director',        image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80', bio: 'Managing financial operations and owner returns' },
  ];

  const guestReviews = [
    { id: 1, name: 'Emily Thompson',  location: 'United States',  rating: 5, text: 'An absolutely incredible experience! The villa was stunning, and the service was impeccable. Dewanta Villas exceeded all our expectations. The team was responsive and helpful throughout our entire stay.',        date: 'October 2024',   avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80' },
    { id: 2, name: 'James Wilson',    location: 'United Kingdom', rating: 5, text: 'Perfect villa management company. They handled everything professionally and made our Bali vacation unforgettable. The attention to detail and personalized service was outstanding.',                               date: 'September 2024', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&q=80' },
    { id: 3, name: 'Sophie Martin',   location: 'Australia',      rating: 5, text: 'We have stayed at multiple villas managed by Dewanta Villas, and each time has been perfect. The quality is consistently high, and the staff goes above and beyond to ensure guest satisfaction.',                 date: 'August 2024',    avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&q=80' },
    { id: 4, name: 'Daniel Lee',      location: 'Singapore',      rating: 5, text: 'Exceptional service from start to finish. The villa was exactly as described, clean, and beautifully maintained. The team was always available to help with any requests. Highly recommended!',                      date: 'July 2024',      avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&q=80' },
    { id: 5, name: 'Maria Garcia',    location: 'Spain',          rating: 5, text: 'Our family had the most wonderful time. The villa was spacious, luxurious, and perfectly located. Dewanta Villas team made sure we had everything we needed for a perfect vacation.',                               date: 'June 2024',      avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&q=80' },
    { id: 6, name: 'Alex Brown',      location: 'Canada',         rating: 5, text: 'Outstanding management company! They were proactive in communication, the villa was immaculate, and they arranged everything we requested. Will definitely book with them again.',                                    date: 'May 2024',       avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80' },
  ];

  const platforms = ['Airbnb', 'Booking.com', 'Agoda', 'Expedia', 'Hotels.com', 'Vrbo', 'Trip.com', 'Traveloka', 'Tiket.com', 'Pegipegi', 'HomeAway', 'TripAdvisor'];

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    const onMouse  = e  => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouse, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('mousemove', onMouse); };
  }, []);

  useEffect(() => {
    const iv = setInterval(() => setCurrentReview(p => (p === guestReviews.length - 1 ? 0 : p + 1)), 5500);
    return () => clearInterval(iv);
  }, []);

  const heroOpacity  = Math.max(0, 1 - scrollY / 580);
  const heroParallax = scrollY * 0.35;
  const visibleTeam  = 3;
  const maxSlide     = teamMembers.length - visibleTeam;

  return (
    <MainLayout>
    <div style={{ minHeight: '100vh', backgroundColor: C.white, overflowX: 'hidden', fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── Global CSS ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }

        @keyframes floatOrb   { 0% { transform: translateY(0) scale(1); } 100% { transform: translateY(-40px) scale(1.1); } }
        @keyframes heroIn     { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmer    { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes pulseDot   { 0%,100% { box-shadow: 0 0 0 0 rgba(217,119,6,0.6); } 50% { box-shadow: 0 0 0 10px rgba(217,119,6,0); } }
        @keyframes bounce     { 0%,100% { transform: translateY(0); opacity: 1; } 50% { transform: translateY(10px); opacity: 0.4; } }
        @keyframes reviewSlide { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes floatBadge  { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes spin        { to { transform: rotate(360deg); } }

        .ph-1 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.20s both; }
        .ph-2 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.45s both; }
        .ph-3 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.70s both; }
        .ph-4 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.90s both; }

        .shimmer-text {
          background: linear-gradient(90deg, #d97706, #f59e0b, #d97706);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }

        .scroll-bounce  { animation: bounce 2s ease-in-out infinite; }
        .float-badge    { animation: floatBadge 6s ease-in-out infinite; }
        .review-animate { animation: reviewSlide 0.6s cubic-bezier(0.16,1,0.3,1) both; }
        .dot-btn        { border: none; cursor: pointer; height: 8px; border-radius: 9999px; transition: width 0.35s ease, background 0.35s ease; }

        .stat-card { transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1); cursor: default; background: transparent !important; }
        .stat-card:hover { transform: translateY(-8px); }

        .op-card { transition: transform 0.45s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.4s ease; }
        .op-card:hover { transform: translateY(-6px); box-shadow: 0 28px 64px rgba(0,0,0,0.1) !important; }

        .cursor-glow {
          pointer-events: none; position: fixed; width: 280px; height: 280px; border-radius: 50%;
          background: radial-gradient(circle, rgba(217,119,6,0.06) 0%, transparent 70%);
          transform: translate(-50%,-50%);
          transition: left 0.12s ease, top 0.12s ease;
          z-index: 9999; mix-blend-mode: multiply;
        }
      `}</style>

      {/* Cursor glow */}
      <div className="cursor-glow" style={{ left: mousePos.x, top: mousePos.y }} />

    

      {/* ══ HERO ══ */}
      <section style={{ position: 'relative', height: '100vh', minHeight: 600, overflow: 'hidden' }}>
        <video ref={videoRef} autoPlay muted loop playsInline style={{
          width: '100%', height: '100%', objectFit: 'cover',
          transform: `translateY(${heroParallax}px)`, willChange: 'transform',
        }}>
          <source src="/images/hero-video.mp4" type="video/mp4" />
          <img src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=1920&q=80" alt="About" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </video>

        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.12), rgba(0,0,0,0.46), rgba(0,0,0,0.74))' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.18), transparent)' }} />
        <Orb size="380px" color="radial-gradient(circle, #d97706, #f59e0b)" top="6%"  left="66%" delay={0} />
        <Orb size="250px" color="radial-gradient(circle, #92400e, #d97706)" top="62%" left="-4%" delay={2} />

        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 24px',
          transform: `translateY(${heroParallax * 0.4}px)`, opacity: heroOpacity,
        }}>
          <div className="ph-1" style={{ marginBottom: 24 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '10px 22px', backgroundColor: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 50, color: '#fff', fontSize: 12, fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: C.amber, animation: 'pulseDot 2s ease-in-out infinite' }} />
              Professional Villa Management • Bali
            </span>
          </div>

          <h1 className="ph-2" style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(36px,8vw,82px)', fontWeight: 700,
            color: '#fff', lineHeight: 1.06, letterSpacing: '-0.02em', marginBottom: 16,
          }}>
            Sit Back and Enjoy<br /><span className="shimmer-text">the Returns</span>
          </h1>

          <p className="ph-3" style={{ fontSize: 'clamp(15px,2vw,19px)', color: 'rgba(255,255,255,0.82)', maxWidth: 540, marginBottom: 10, fontWeight: 300, lineHeight: 1.72 }}>
            Premium villa management powered by authentic Balinese hospitality
          </p>
          <p className="ph-3" style={{ fontSize: 14, color: 'rgba(255,255,255,0.46)', marginBottom: 38, letterSpacing: '0.07em' }}>
            PT. Baswara Arta Santosa
          </p>

          <div className="ph-4" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
            <BtnPrimary onClick={() => document.getElementById('our-story')?.scrollIntoView({ behavior: 'smooth' })}>
              Our Story <ArrowRight size={17} />
            </BtnPrimary>
            <BtnOutline onClick={() => window.location.href = '/contact'} dark>
              Get in Touch
            </BtnOutline>
          </div>
        </div>

        <div className="scroll-bounce" style={{ position: 'absolute', bottom: 34, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.55)' }}>
          <span style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase' }}>Scroll</span>
          <ChevronDown size={18} />
        </div>
      </section>

      {/* ══ STATS STRIP ══ */}
      <section style={{ padding: '56px 24px', backgroundColor: C.gray900, position: 'relative', overflow: 'hidden' }}>
        <Orb size="220px" color="radial-gradient(circle, rgba(217,119,6,0.32), transparent)" top="-50%" left="28%" />
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: 32, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {[
            { end: 50,  suffix: '+', label: 'Managed Villas',     sub: 'Premium Properties' },
            { end: 25,  suffix: '%', label: 'Booking Increase',   sub: 'Average Growth'     },
            { end: 40,  suffix: '%', label: 'Higher Occupancy',   sub: 'Rate Improvement'   },
            { end: 95,  suffix: '%', label: 'Guest Satisfaction', sub: '5-Star Reviews'     },
          ].map((s, i) => (
            <Anim key={i} delay={i * 100}>
              <div className="stat-card" style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: '8px' }}>
                <div className="shimmer-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 52, fontWeight: 700, marginBottom: 4 }}>
                  <CountUp end={s.end} suffix={s.suffix} />
                </div>
                <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 13.5, fontWeight: 600 }}>{s.label}</div>
                <div style={{ color: 'rgba(255,255,255,0.34)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 2 }}>{s.sub}</div>
              </div>
            </Anim>
          ))}
        </div>
      </section>

      {/* ══ OUR STORY ══ */}
      <section id="our-story" style={{ padding: '112px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="520px" color="radial-gradient(circle, rgba(251,191,36,0.08), transparent)" top="0%" left="-8%" delay={3} />
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px,1fr))', gap: 80, alignItems: 'center', position: 'relative', zIndex: 1 }}>

          {/* Left — image */}
          <Anim dir="left" delay={0}>
            <div style={{ position: 'relative' }}>
              <div style={{ borderRadius: 24, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.18)', transition: 'transform 0.8s ease' }}
                onMouseEnter={e => e.currentTarget.querySelector('img').style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.querySelector('img').style.transform = 'scale(1)'}
              >
                <img
                  src="/images/atas.jpg" alt="Our Story"
                  style={{ width: '100%', display: 'block', objectFit: 'cover', transition: 'transform 0.8s ease' }}
                  onError={e => { e.target.src = 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=800&q=80'; }}
                />
              </div>
              <div style={{ position: 'absolute', bottom: -12, right: -12, width: 120, height: 120, backgroundColor: C.amber, borderRadius: 20, zIndex: -1, opacity: 0.14 }} />
              <div className="float-badge" style={{
                position: 'absolute', bottom: -40, left: -40,
                backgroundColor: C.white, borderRadius: 20,
                boxShadow: '0 20px 50px rgba(0,0,0,0.12)', padding: '18px 22px',
                border: `1px solid ${C.gray100}`, display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: C.amberLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🏝️</div>
                <div>
                  <div style={{ fontSize: 10.5, color: C.gray400, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Local Balinese</div>
                  <div style={{ fontWeight: 700, color: C.gray900, fontSize: 14.5 }}>Company</div>
                </div>
              </div>
            </div>
          </Anim>

          {/* Right — text */}
          <div>
            <Anim dir="right" delay={0}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 }}>
                <div style={{ width: 40, height: 3, backgroundColor: C.amber, borderRadius: 2 }} />
                <span style={{ padding: '6px 16px', backgroundColor: C.amberLight, color: C.amberDark, borderRadius: 50, fontSize: 13, fontWeight: 700 }}>Our Story</span>
              </div>
            </Anim>
            <Anim dir="right" delay={80}>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(30px,5vw,50px)', fontWeight: 700, color: C.gray900, lineHeight: 1.12, marginBottom: 20 }}>
                About Dewanta<br /><span style={{ color: C.amber }}>Villas Management</span>
              </h2>
            </Anim>
            <Anim dir="right" delay={150}>
              <p style={{ fontSize: 15.5, color: C.gray600, lineHeight: 1.88, marginBottom: 8 }}>
                Dewanta Property is a local Balinese company specializing in premium villa management services. We combine authentic hospitality with modern professional standards to deliver exceptional experiences for both property owners and guests.
              </p>
              <p style={{ fontSize: 14.5, color: C.gray500, lineHeight: 1.88, marginBottom: 34 }}>
                As a fully local brand, we understand Bali's unique culture, regulations, and guest expectations — delivering authentic experiences with international standards.
              </p>
            </Anim>
            <Anim dir="right" delay={210}>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: C.gray900, marginBottom: 16 }}>What We Stand For</h3>
              <ValueRow Icon={Heart}  iconColor="#d97706" iconBg={C.amberLight} label="Friendly Service" desc="Personal touch in every interaction" />
              <ValueRow Icon={Award}  iconColor="#2563eb" iconBg="#dbeafe"       label="Comfort"          desc="Creating memorable stays for every guest" />
              <ValueRow Icon={Shield} iconColor="#16a34a" iconBg="#dcfce7"       label="Trust"            desc="Reliable partnership for property owners" />
            </Anim>
          </div>
        </div>
      </section>

      {/* ══ WHY LOCAL ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.stone50, position: 'relative', overflow: 'hidden' }}>
        <Orb size="420px" color="radial-gradient(circle, rgba(217,119,6,0.08), transparent)" top="8%" left="72%" delay={1} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 60 }}>
              <SectionLabel text="Why Local Matters" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900, marginBottom: 12 }}>
                Your Trusted Local Partner
              </h2>
              <p style={{ fontSize: 16.5, color: C.gray500, maxWidth: 620, margin: '0 auto', lineHeight: 1.78 }}>
                As a fully local brand, we understand Bali's unique culture, regulations, and guest expectations — delivering authentic experiences with international standards.
              </p>
            </div>
          </Anim>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 22 }}>
            <WhyCard emoji="🏝️"            iconBg={C.amberLight}  title="Born & Raised in Bali"    desc="Deep understanding of local market dynamics, regulations, and guest preferences."          delay={0}   />
            <WhyCard Icon={TrendingUp} iconColor="#16a34a" iconBg="#dcfce7" title="Measurable Results"        desc="Average 25% increase in bookings, 40% higher occupancy, and 95% guest satisfaction."    delay={100} />
            <WhyCard Icon={Users}      iconColor="#2563eb" iconBg="#dbeafe" title="Experienced Professionals" desc="Certified managers, trained hospitality staff, and 24/7 support with global standards." delay={200} />
            <WhyCard Icon={Zap}        iconColor="#7c3aed" iconBg="#ede9fe" title="Smart Management"          desc="Advanced booking systems, automated pricing tools, and real-time reporting dashboard."    delay={300} />
          </div>
        </div>
      </section>

      {/* ══ TEAM ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="360px" color="radial-gradient(circle, rgba(217,119,6,0.07), transparent)" top="20%" left="-5%" delay={2} />
        <div style={{ maxWidth: 1160, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <SectionLabel text="Our Team" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900, marginBottom: 12 }}>
                Meet Our Dedicated Team
              </h2>
              <p style={{ fontSize: 16.5, color: C.gray500, maxWidth: 520, margin: '0 auto' }}>
                Behind every smooth check-in, five-star review, and happy villa owner
              </p>
            </div>
          </Anim>

          <div style={{ position: 'relative' }}>
            <div style={{ overflow: 'hidden', borderRadius: 16 }}>
              <div style={{
                display: 'flex', gap: 24,
                transform: `translateX(-${currentTeamSlide * (100 / visibleTeam + 24 / visibleTeam)}%)`,
                transition: 'transform 0.65s cubic-bezier(0.34,1.56,0.64,1)',
              }}>
                {teamMembers.map(m => (
                  <div key={m.id} style={{ flex: `0 0 calc(${100 / visibleTeam}% - 16px)` }}>
                    <TeamCard member={m} />
                  </div>
                ))}
              </div>
            </div>

            <div style={{ position: 'absolute', top: '50%', left: -28, transform: 'translateY(-50%)' }}>
              <NavBtn onClick={() => setCurrentTeamSlide(p => Math.max(0, p - 1))} disabled={currentTeamSlide === 0}>
                <ChevronLeft size={22} color={C.gray600} />
              </NavBtn>
            </div>
            <div style={{ position: 'absolute', top: '50%', right: -28, transform: 'translateY(-50%)' }}>
              <NavBtn onClick={() => setCurrentTeamSlide(p => Math.min(maxSlide, p + 1))} disabled={currentTeamSlide === maxSlide}>
                <ChevronRight size={22} color={C.gray600} />
              </NavBtn>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 28 }}>
            {Array.from({ length: maxSlide + 1 }).map((_, i) => (
              <button key={i} className="dot-btn" onClick={() => setCurrentTeamSlide(i)}
                style={{ width: i === currentTeamSlide ? 32 : 8, backgroundColor: i === currentTeamSlide ? C.amber : C.gray200 }} />
            ))}
          </div>
        </div>
      </section>

      {/* ══ BOOKING PLATFORMS ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.stone50, position: 'relative', overflow: 'hidden' }}>
        <Orb size="460px" color="radial-gradient(circle, rgba(217,119,6,0.07), transparent)" top="30%" left="66%" delay={0} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <SectionLabel text="Our Support System" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900, marginBottom: 12 }}>
                Connected to 15+ Global Platforms
              </h2>
              <p style={{ fontSize: 16.5, color: C.gray500, maxWidth: 520, margin: '0 auto' }}>
                We maximize your property's visibility across the world's leading booking platforms
              </p>
            </div>
          </Anim>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px,1fr))', gap: 16, marginBottom: 16 }}>
            {platforms.map((p, i) => <PlatformBadge key={p} name={p} delay={Math.min(i, 8) * 50} />)}
          </div>

          <Anim delay={200}>
            <div style={{ background: 'linear-gradient(135deg, #fffbeb, #fff7ed)', borderRadius: 18, border: '2px solid rgba(217,119,6,0.22)', padding: '22px', textAlign: 'center' }}>
              <span className="shimmer-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 700 }}>+ 11 More Platforms</span>
              <span style={{ fontSize: 14, color: C.gray500, marginLeft: 12 }}>and growing</span>
            </div>
          </Anim>
        </div>
      </section>

      {/* ══ WHAT WE OFFER ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="500px" color="radial-gradient(circle, rgba(251,191,36,0.07), transparent)" top="-6%" left="-5%" delay={4} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <SectionLabel text="What We Offer" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900 }}>
                Comprehensive Villa Management Solutions
              </h2>
            </div>
          </Anim>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px,1fr))', gap: 24 }}>
            <OfferCard Icon={TrendingUp} iconBg="#d97706" bgFrom="#fffbeb" bgTo="#fff7ed" borderColor="rgba(217,119,6,0.2)"  title="Maximize ROI"              desc="We help maximize villa occupancy to give the best return on investment for property owners." delay={0}   />
            <OfferCard Icon={Heart}      iconBg="#2563eb" bgFrom="#eff6ff" bgTo="#e0f2fe" borderColor="rgba(37,99,235,0.2)"  title="24/7 Guest Support"       desc="24-hour guest communication service to make sure every guest gets full assistance during their stay." delay={80}  />
            <OfferCard Icon={Award}      iconBg="#16a34a" bgFrom="#f0fdf4" bgTo="#dcfce7" borderColor="rgba(22,163,74,0.2)"  title="Professional Maintenance" desc="Our dedicated maintenance team ensures the villa is always well-maintained and in excellent condition." delay={160} />
            <OfferCard Icon={Users}      iconBg="#7c3aed" bgFrom="#faf5ff" bgTo="#ede9fe" borderColor="rgba(124,58,237,0.2)" title="Dedicated Manager"        desc="Professional villa manager handling check-ins, quality control, and guest assistance." delay={0}   />
            <OfferCard Icon={Target}     iconBg="#4f46e5" bgFrom="#eef2ff" bgTo="#e0e7ff" borderColor="rgba(79,70,229,0.2)"  title="Staff Training"           desc="Regular training with experienced professional trainers to maintain high hospitality standards." delay={80}  />
            <OfferCard Icon={Zap}        iconBg="#dc2626" bgFrom="#fff1f2" bgTo="#fee2e2" borderColor="rgba(220,38,38,0.2)"  title="Global Booking Platforms" desc="Connected to 15+ global platforms including Airbnb, Booking.com, Agoda, and more." delay={160} />
          </div>
        </div>
      </section>

      {/* ══ PACKAGES ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.stone50, position: 'relative', overflow: 'hidden' }}>
        <Orb size="400px" color="radial-gradient(circle, rgba(217,119,6,0.08), transparent)" top="0%" left="76%" delay={1} />
        <div style={{ maxWidth: 1160, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <SectionLabel text="Services Packages" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900 }}>
                Choose Your Management Package
              </h2>
            </div>
          </Anim>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px,1fr))', gap: 28, marginBottom: 28 }}>
            <PackageCard
              popular
              title="Full Management Services"
              subtitle="Complete villa management solution for maximum returns"
              price="15% + Support Fee"
              priceNote="Charged from published rate after OTA fees deducted"
              accentColor={C.amber} accentHover={C.amberHover}
              delay={0}
              items={[
                'Monthly Revenue Report — Detailed financial tracking and insights',
                'Staff Training & Development — Continuous professional development programs',
                'Guest Check-in/Check-out — Seamless arrival and departure experience',
                'Issue Resolution — 24/7 complaint handling and problem solving',
                'Dynamic Pricing System — Market-optimized rate management',
                'Dedicated Villa Manager — Professional oversight and guest assistance',
              ]}
            />
            <PackageCard
              title="Digital Marketing Package"
              subtitle="Global reach across 15+ booking platforms"
              price="15% from Published Rate"
              priceNote="+ Support System Fee"
              accentColor="#2563eb" accentHover="#1d4ed8"
              delay={120}
              items={[
                'Payment Collection — Secure booking and revenue management',
                'Global Distribution — 15+ partner platforms coverage',
                'Listing Optimization — Professional content creation and management',
                'Guest Application — Mobile-friendly booking experience',
                '24/7 Guest Communication — Personalized concierge services',
                'Dynamic Pricing — Market analysis and rate optimization',
                'Calendar Sync — Real-time availability management',
                'Reservation System — Streamlined booking management',
              ]}
            />
          </div>

          {/* Operational Package */}
          <Anim delay={240}>
            <div className="op-card" style={{
              background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
              borderRadius: 24, padding: '40px',
              border: '2px solid rgba(22,163,74,0.2)',
              boxShadow: '0 6px 28px rgba(0,0,0,0.07)',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 36, alignItems: 'center' }}>
                <div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, fontWeight: 700, color: C.gray900, marginBottom: 6 }}>Operational Package</h3>
                  <p style={{ fontSize: 14.5, color: C.gray500, marginBottom: 16 }}>Professional operations management</p>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 700, color: '#16a34a', marginBottom: 6 }}>Starting from IDR 3,000,000</div>
                  <p style={{ fontSize: 13, color: C.gray500 }}>Professional management without the full-service commitment</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {['Monthly Revenue Report', 'Regular Staff Training', '24/7 Guest Communication', 'Property Maintenance Inspection', 'Dedicated Professional Villa Manager'].map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>✓</span>
                      </div>
                      <span style={{ fontSize: 13.5, color: C.gray700 }}>{item}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <OpBtn />
                </div>
              </div>
            </div>
          </Anim>
        </div>
      </section>

      {/* ══ REVIEWS ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="420px" color="radial-gradient(circle, rgba(251,191,36,0.08), transparent)" top="0%" left="-5%" delay={2} />
        <div style={{ maxWidth: 820, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <SectionLabel text="Guest Reviews" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900 }}>
                What Our Guests Say
              </h2>
            </div>
          </Anim>

          <Anim delay={80}>
            <div style={{ position: 'relative' }}>
              <div
                key={currentReview}
                className="review-animate"
                style={{
                  background: 'linear-gradient(135deg, #fffbeb, #fff7ed)',
                  borderRadius: 26, padding: '52px 48px',
                  boxShadow: '0 16px 60px rgba(0,0,0,0.1)',
                  border: '1.5px solid rgba(217,119,6,0.16)',
                  position: 'relative', overflow: 'hidden',
                }}
              >
                <Orb size="200px" color="radial-gradient(circle, rgba(217,119,6,0.1), transparent)" top="-20%" left="70%" delay={0} />
                <Quote size={44} color={C.amber} style={{ marginBottom: 24, opacity: 0.8 }} />
                <div style={{ display: 'flex', gap: 3, marginBottom: 22 }}>
                  {Array.from({ length: guestReviews[currentReview].rating }).map((_, i) => (
                    <Star key={i} size={18} fill="#fbbf24" color="#fbbf24" />
                  ))}
                </div>
                <p style={{ fontSize: 18, color: C.gray700, lineHeight: 1.82, marginBottom: 32, fontStyle: 'italic', position: 'relative', zIndex: 1 }}>
                  "{guestReviews[currentReview].text}"
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                  <img
                    src={guestReviews[currentReview].avatar}
                    alt={guestReviews[currentReview].name}
                    style={{ width: 62, height: 62, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${C.amber}`, flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontWeight: 700, color: C.gray900, fontSize: 15.5 }}>{guestReviews[currentReview].name}</div>
                    <div style={{ color: C.gray500, fontSize: 13.5 }}>{guestReviews[currentReview].location}</div>
                    <div style={{ color: C.gray400, fontSize: 12.5, marginTop: 2 }}>{guestReviews[currentReview].date}</div>
                  </div>
                </div>
              </div>

              <div style={{ position: 'absolute', top: '50%', left: -28, transform: 'translateY(-50%)' }}>
                <NavBtn onClick={() => setCurrentReview(p => (p === 0 ? guestReviews.length - 1 : p - 1))}>
                  <ChevronLeft size={22} color={C.gray600} />
                </NavBtn>
              </div>
              <div style={{ position: 'absolute', top: '50%', right: -28, transform: 'translateY(-50%)' }}>
                <NavBtn onClick={() => setCurrentReview(p => (p === guestReviews.length - 1 ? 0 : p + 1))}>
                  <ChevronRight size={22} color={C.gray600} />
                </NavBtn>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 28 }}>
              {guestReviews.map((_, i) => (
                <button key={i} className="dot-btn" onClick={() => setCurrentReview(i)}
                  style={{ width: i === currentReview ? 32 : 8, backgroundColor: i === currentReview ? C.amber : C.gray200 }} />
              ))}
            </div>
          </Anim>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.gray900, position: 'relative', overflow: 'hidden' }}>
        <Orb size="500px" color="radial-gradient(circle, rgba(217,119,6,0.22), transparent)" top="0%"  left="46%" delay={0} />
        <Orb size="300px" color="radial-gradient(circle, rgba(251,191,36,0.12), transparent)" top="55%" left="-3%" delay={3} />
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <p style={{ color: C.amber, fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 20 }}>Ready to Start?</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(32px,6vw,60px)', fontWeight: 700, color: '#fff', lineHeight: 1.1, marginBottom: 18 }}>
              Ready to Maximize Your<br /><span className="shimmer-text">Villa Investment?</span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16, marginBottom: 42, lineHeight: 1.78 }}>
              Contact us today for a free consultation and discover how we can help you achieve better returns on your property.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <BtnPrimary href="mailto:dewanta.villas@gmail.com" style={{ padding: '15px 32px', fontSize: 16 }}>
                Email Us <ArrowRight size={17} />
              </BtnPrimary>
              <BtnOutline href="https://wa.me/6287785686777" dark style={{ padding: '14px 32px', fontSize: 16 }}>
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                +62 877 8568 6777
              </BtnOutline>
            </div>
          </Anim>
        </div>
      </section>


    </div>
    </MainLayout>
  );
};

export default ProfilePage;